import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PermissionCatalogEntry, RoleGrant } from "@/features/users/api/users.api";

const KIND_TOOLTIP: Record<string, string> = {
  display: "Display setting — controls what's shown in the UI. Not access control.",
  preference: "Preference — controls notifications this user receives. Not access control.",
  eligibility: "Eligibility — whether this user can be selected or @mentioned by others. Not access control.",
};

export interface PermissionEditorProps {
  catalog: readonly PermissionCatalogEntry[];
  grants: Map<string, RoleGrant>;
  onChange: (next: Map<string, RoleGrant>) => void;
}

export function PermissionEditor({ catalog, grants, onChange }: PermissionEditorProps) {

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionCatalogEntry[]>();
    for (const entry of catalog) {
      if (entry.disabled) continue;
      let bucket = map.get(entry.category);
      if (!bucket) { bucket = []; map.set(entry.category, bucket); }
      bucket.push(entry);
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => compareCode(a.code, b.code));
    }
    return Array.from(map.entries()).sort(
      ([, a], [, b]) => compareCode(a[0].code, b[0].code),
    );
  }, [catalog]);

  function toggle(entry: PermissionCatalogEntry, field: "canUse" | "canReassign", checked: boolean) {
    const next = new Map(grants);
    const current = next.get(entry.id) ?? { permissionId: entry.id, canUse: false, canReassign: false };
    let canUse = current.canUse;
    let canReassign = current.canReassign;
    if (field === "canUse") {
      canUse = checked;
      if (!canUse) canReassign = false;
    } else {
      canReassign = checked;
      if (canReassign) canUse = true;
    }
    if (!canUse && !canReassign) {
      next.delete(entry.id);
    } else {
      next.set(entry.id, { permissionId: entry.id, canUse, canReassign });
    }
    onChange(next);
  }

  function bulkSelectCategory(category: string, on: boolean) {
    const next = new Map(grants);
    for (const entry of catalog) {
      if (entry.category !== category || entry.disabled || entry.displayOnly) continue;
      if (on) {
        next.set(entry.id, { permissionId: entry.id, canUse: true, canReassign: false });
      } else {
        next.delete(entry.id);
      }
    }
    onChange(next);
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        {grouped.map(([category, entries]) => {
          const togglableCount = entries.filter((e) => !e.displayOnly).length;
          const grantedCount = entries.filter((e) => !e.displayOnly && grants.has(e.id)).length;
          const allOn = togglableCount > 0 && grantedCount === togglableCount;
          return (
            <Card key={category} className="overflow-hidden">
              <header className="flex items-center justify-between px-4 py-2 border-b">
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-slate-600">{category}</h3>
                  <p className="text-xs text-muted-foreground">
                    {grantedCount} of {togglableCount} granted
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => bulkSelectCategory(category, true)} disabled={allOn}>
                    Select all
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => bulkSelectCategory(category, false)} disabled={grantedCount === 0}>
                    Clear
                  </Button>
                </div>
              </header>
              <div>
                {entries.map((entry) => (
                  <Row
                    key={entry.id}
                    entry={entry}
                    grant={grants.get(entry.id)}
                    onToggle={(field, checked) => toggle(entry, field, checked)}
                  />
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function Row({
  entry,
  grant,
  onToggle,
}: {
  entry: PermissionCatalogEntry;
  grant: RoleGrant | undefined;
  onToggle: (field: "canUse" | "canReassign", checked: boolean) => void;
}) {

  const depth = entry.code < 100 ? 0 : entry.code < 10_000 ? 1 : 2;
  const indentPx = depth * 20;

  if (entry.displayOnly) {
    return (
      <div
        className="border-b border-slate-100 px-4 py-2 text-xs uppercase tracking-wider text-slate-500"
        style={{ paddingLeft: 16 + indentPx }}
      >
        {entry.name}
      </div>
    );
  }

  const nonAccessKind = entry.kind && entry.kind !== "access" ? entry.kind : null;

  return (
    <div
      className="border-b border-slate-100 px-4 py-1.5 flex items-center gap-3 hover:bg-slate-50"
      style={{ paddingLeft: 16 + indentPx }}
    >
      <code className="text-[10px] font-mono text-slate-400 w-16 shrink-0 tabular-nums">
        {formatCode(entry.code)}
      </code>
      <div className="flex-1 min-w-0">
        <div className="flex items-center text-sm text-slate-800">
          <span>{entry.name}</span>
          {nonAccessKind && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-1.5 shrink-0 text-[10px] leading-none uppercase tracking-wide text-slate-400 cursor-help">
                  {`- ${nonAccessKind}`}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-56">{KIND_TOOLTIP[nonAccessKind]}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {entry.description && (
          <div className="text-xs text-slate-500 mt-0.5">{entry.description}</div>
        )}
      </div>
      <label className="flex items-center gap-1.5 text-xs text-slate-700 shrink-0 cursor-pointer">
        <Checkbox
          checked={grant?.canUse ?? false}
          onCheckedChange={(c) => onToggle("canUse", c === true)}
        />
        Use
      </label>
      <label className="flex items-center gap-1.5 text-xs text-slate-700 shrink-0 cursor-pointer">
        <Checkbox
          checked={grant?.canReassign ?? false}
          onCheckedChange={(c) => onToggle("canReassign", c === true)}
        />
        Reassign
      </label>
    </div>
  );
}

function codePath(code: number): [number, number, number] {
  if (code < 100) return [code, -1, -1];
  if (code < 10_000) return [Math.floor(code / 100), code % 100, -1];
  return [Math.floor(code / 10_000), Math.floor(code / 100) % 100, code % 100];
}

function compareCode(a: number, b: number): number {
  const pa = codePath(a);
  const pb = codePath(b);
  return pa[0] - pb[0] || pa[1] - pb[1] || pa[2] - pb[2];
}

function formatCode(code: number): string {
  if (code < 100) return code.toString().padStart(2, "0");
  if (code < 10_000) {
    const a = Math.floor(code / 100);
    const b = code % 100;
    return `${a.toString().padStart(2, "0")}.${b.toString().padStart(2, "0")}`;
  }
  const a = Math.floor(code / 10_000);
  const b = Math.floor(code / 100) % 100;
  const c = code % 100;
  return `${a.toString().padStart(2, "0")}.${b.toString().padStart(2, "0")}.${c.toString().padStart(2, "0")}`;
}
